import { Component, Input, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DAO } from 'src/app/modal/DAO';
import { QuestionAnswer, Questionnaire, QuestionOption } from 'src/app/modal/questionnaire.models';
import { ConfirmationDialogService } from 'src/app/services/confirmationDialog.service';
import { ProductService } from 'src/app/services/product.service';

@Component({
    selector: 'default-coverage',
    templateUrl: './default-coverage.component.html',
    styleUrls: ['./default-coverage.component.css']
})
export class DefaultCoverageComponent implements OnInit {

    @Input() questionnaire: Questionnaire;
    @Input() isFormSubmitted: boolean;

    public liabilityLimit: QuestionAnswer;
    public mpLimit: QuestionAnswer;
    public medExpenseBenefits: QuestionAnswer;
    public incomeLoss: QuestionAnswer;
    public tortLimitSelect: QuestionAnswer;
    public tortLimits: QuestionAnswer;
    public Q_addedPIP: QuestionAnswer;
    public addedPIP: QuestionAnswer;
    public umReject: QuestionAnswer;

    public validationMessage: string = "";
    public accountLegalEntityAnswer: string;
    private destroy$ = new Subject<void>();
    private readonly REJECTED_TORT_LIMIT_CONFIRMATION: string = "RejectedTortLimitConfirmation";
    public originalTortLimitsQuestionOptions: QuestionOption[];
    private readonly A_TORT_LIMIT_REJ_NOT_ELIGIBLE: string = "A-Tort Limit Rej, Not Eligible";
    private readonly B_INSURED_AND_HALF_OP_ACCEPTED: string = "B-Insured and half op accepted";

    constructor(private productService: ProductService,
        private messageService: MessageService,
        private dao: DAO, private confirmationDialogService: ConfirmationDialogService
    ) { }

    public ngOnInit(): void {
        this.loadQuestionAnswers();
        this.loadValidationMessage();
        this.confirmationDialogService.confirmationDialogData.pipe(takeUntil(this.destroy$)).subscribe((message: any) => {
            if (message?.data == this.REJECTED_TORT_LIMIT_CONFIRMATION) {
                if (message?.response === 'Yes') {
                    this.tortLimitsRejected();
                }
                else {
                    this.tortLimitsAccepted();
                }
                this.confirmationDialogService.updateDialogData(null);
            }
        });
    }

    private loadQuestionAnswers(): void {
        this.liabilityLimit = this.LoadAnswerFromDefault('liabilityLimit');
        this.mpLimit = this.LoadAnswerFromDefault('mpLimit');
        this.medExpenseBenefits = this.LoadAnswerFromDefault('medExpenseBenefits');
        this.incomeLoss = this.LoadAnswerFromDefault('incomeLoss');
        this.tortLimitSelect = this.LoadAnswerFromDefault('tortLimitSelect');
        this.tortLimits = this.LoadAnswerFromDefault('tortLimits');
        this.Q_addedPIP = this.LoadAnswerFromDefault('Q_addedPIP');
        this.addedPIP = this.LoadAnswerFromDefault('addedPIP');
        this.umReject = this.LoadAnswerFromDefault('umReject');
     
        this.accountLegalEntityAnswer = this.productService.getAccountLegalEntityAnswer();
        if (this.tortLimitSelect.enabled) {
            this.originalTortLimitsQuestionOptions = this.tortLimits.questionOptions;
            if (this.tortLimits.questionOptions.some(x => x.label === this.productService.INSURED_AND_ALL_REGULAR_OPERATORS_ACCEPT_TORT_LIMITATION)) {
                if (this.tortLimitSelect.answer == "Yes") {
                    this.tortLimitsAccepted();
                }else if((this.tortLimitSelect.answer == "No")){
                    this.tortLimitsRejected();
                }
            }
            this.tortLimits.enabled = this.tortLimitSelect.answer != '';
        }
    }

    public LoadAnswerFromDefault(key: string): QuestionAnswer {
        var defaultQuestionAnswer = this.getDefaultQuestionAnswer();
        let questionAnswer = this.questionnaire.questionAnswer.find(x => x.key === key) ?? defaultQuestionAnswer;
        if (!!!questionAnswer?.answer && questionAnswer?.questionDefault?.length > 0) {
            questionAnswer.answer = questionAnswer.questionDefault[0].code;
        }
        else if (!!!questionAnswer?.answer && questionAnswer?.questionDefault?.length == 0 
              && questionAnswer?.questionOptions[0]?.code.startsWith("CLICK")) 
        {
            questionAnswer.answer = questionAnswer?.questionOptions[0]?.code;
        }
        else if (!!questionAnswer?.answer && questionAnswer?.questionDefault?.length > 0 
                    && !!!questionAnswer?.questionOptions?.some(x => x.code == questionAnswer?.answer))
        {
             questionAnswer.answer = questionAnswer.questionDefault[0].code;
        }
        return questionAnswer;
    }

    private getDefaultQuestionAnswer(): QuestionAnswer {
        return {
            answer: null,
            enabled: false,
            required: false,
            group: null,
            hidden: false,
            key: null,
            isVehicleUpdatedbyVEH1: false,
            minimum: null,
            name: null,
            type: '',
            rowIndex: 0,
            questionOptions: []
        }
    }
    
    public tortLimitSelection(): void {
        if (this.tortLimitSelect.answer == 'Yes') {
            //tortLimitSelect is Accepted
            if (this.tortLimits.questionOptions.some(x => x.label === this.productService.INSURED_AND_ALL_REGULAR_OPERATORS_ACCEPT_TORT_LIMITATION)) {
                this.tortLimitsAccepted();
            }
            else {
                this.tortLimits.enabled = true;
                this.tortLimits.answer = this.tortLimits.questionOptions[3].value;
                this.Q_addedPIP.answer = "";
                this.addedPIP.answer = 'CLICK';
            }
        }
        else {
            //tortLimitSelect is Rejected
            if (this.tortLimits.questionOptions.some(x => x.label === this.productService.INSURED_AND_ALL_REGULAR_OPERATORS_ACCEPT_TORT_LIMITATION)) {
                document.body.style.pointerEvents = "none";
                this.messageService.add({ key: 'confirmationPopup', sticky: true, severity: 'warn', data: this.REJECTED_TORT_LIMIT_CONFIRMATION, summary: 'No Fault coverage will not be available when Tort Limitation is rejected. Would you like to continue?' });
            }
            else {
                this.tortLimits.enabled = true;
                this.tortLimits.answer = this.tortLimits.questionOptions[0].value;
            }
        }
        if ((this.tortLimitSelect.answer == 'No' && this.tortLimits.answer == this.B_INSURED_AND_HALF_OP_ACCEPTED)
            || (this.tortLimitSelect.answer == 'No' && this.tortLimits.answer == this.productService.C_INSURED_AND_ALL_OP_ACCEPTED)
            || (this.tortLimitSelect.answer == 'Yes')) {
            this.mpLimit.answer = 'CLICK';
        }
    }

    public invalidTortLimits(): void {
        this.Q_addedPIP.answer = "";
        this.addedPIP.answer = 'CLICK';
            if (!(this.tortLimitSelect.answer == 'No' && this.tortLimits.answer == this.A_TORT_LIMIT_REJ_NOT_ELIGIBLE)) {
                this.mpLimit.answer = 'CLICK';
            }
        }
        
    public get tortLimitValidation() {
        return this.tortLimitSelect.answer == 'No' && this.tortLimits.answer == 'C-Insured and all op accepted';
    }
    private loadValidationMessage(): void {
        this.validationMessage = this.tortLimits.questionOptions.some(x => x.label === this.productService.INSURED_AND_ALL_REGULAR_OPERATORS_ACCEPT_TORT_LIMITATION) ? "" : "This option is not valid when Tort Limitation is rejected";
    }
    public resetUMCoverage(): void {
        var umbi = this.questionnaire.questionAnswer.find(x => x.key === 'umBiLimit');
        var umStacked = this.questionnaire.questionAnswer.find(x => x.key === 'umStacked');
        if (umbi && umStacked) {
            umbi.answer = umbi?.questionDefault?.length > 0 ? umbi.questionDefault[0]?.code : "";
            umStacked.answer = "";
        }
    }
    public tortLimitsRejected(): void {
        this.tortLimits.questionOptions = this.originalTortLimitsQuestionOptions;
        this.tortLimitSelect.answer = "No";
        this.tortLimits.enabled = true;
        this.tortLimits.answer = this.A_TORT_LIMIT_REJ_NOT_ELIGIBLE;
        this.invalidTortLimits();

        // set default value to 'nfPipDed' answer
        if (this.accountLegalEntityAnswer == 'INDIVIDUAL') {
            if (this.productService.productModel.productDetails.nfPipDed.questionDefault.length > 0)
                this.productService.productModel.productDetails.nfPipDed.answer = this.productService.productModel.productDetails.nfPipDed.questionDefault[0].code;
        }

        //dispatch the tortLimitSelection Event
        const tortEvent = new CustomEvent(this.productService.TORT_LIMIT_SELECTED, {
            detail: this.tortLimitSelect.answer
        });
        let element = document.getElementById("tortLimitSelectYes");
        if (!!element) {
            element.dispatchEvent(tortEvent);
        }


        document.body.style.pointerEvents = "visible";
        this.messageService.clear();
    }
    public tortLimitsAccepted(): void {
        this.tortLimits.questionOptions = this.originalTortLimitsQuestionOptions.filter(x => x.code != this.A_TORT_LIMIT_REJ_NOT_ELIGIBLE);
        this.tortLimitSelect.answer = "Yes";
        this.invalidTortLimits();
        this.tortLimits.enabled = true;
        this.tortLimits.answer = this.tortLimits.answer == this.A_TORT_LIMIT_REJ_NOT_ELIGIBLE ? 'CLICK' : this.tortLimits.answer;

        //dispatch the tortLimitSelection Event
        const tortEvent = new CustomEvent(this.productService.TORT_LIMIT_SELECTED, {
            detail: this.tortLimitSelect.answer
        });
        let element = document.getElementById("tortLimitSelectYes");
        if (!!element) {
            element.dispatchEvent(tortEvent);
        }

        document.body.style.pointerEvents = "visible";
        this.messageService.clear();
    }

    public get isTortLimitsDisabled(): boolean {
        let isNewVersion: boolean = this.tortLimits.questionOptions.some(x => x.label === this.productService.INSURED_AND_ALL_REGULAR_OPERATORS_ACCEPT_TORT_LIMITATION);
        return (isNewVersion && this.tortLimits.answer == this.A_TORT_LIMIT_REJ_NOT_ELIGIBLE)
            || (!isNewVersion && this.tortLimitSelect.enabled && this.tortLimitSelect.answer == "Yes")
    }

    public get showMedicalPayment(): boolean {
        let hasTortlimits: boolean = this.questionnaire?.questionAnswer.some(x => x.key === 'tortLimits'); // tortLimits is only available to KY state
        if (!hasTortlimits) {
            //other than KY state
            return this.mpLimit.enabled;
        }
        else {
            if (this.tortLimitSelect.answer == 'Yes') {
                return false;
            }
            return (this.tortLimitSelect.answer == ''
                || this.tortLimitSelect.answer == 'No' && this.tortLimits.answer == this.A_TORT_LIMIT_REJ_NOT_ELIGIBLE);
        }
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}